import type { LearningExercise, SourceRef } from "../types";
import { makeCloze, makeMulti, makeOrdering, makeSingle } from "./legalExerciseFactory";

const pathId = "epu-eqe" as const;

const sources: Record<string, SourceRef> = {
  pct19: { id: "pct19", label: "Art. 19 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a19.html" },
  pct22: { id: "pct22", label: "Art. 22 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a22.html" },
  pct31: { id: "pct31", label: "Art. 31 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a31.html" },
  pct33: { id: "pct33", label: "Art. 33 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a33.html" },
  pct34: { id: "pct34", label: "Art. 34 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a34.html" },
  pct39: { id: "pct39", label: "Art. 39 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a39.html" },
  r90bispct: { id: "r90bispct", label: "R. 90bis PCT", url: "https://www.wipo.int/pct/en/texts/rules/r90bis.html" },
  r159epc: { id: "r159epc", label: "R. 159 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r159.html" },
  r164epc: { id: "r164epc", label: "R. 164 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r164.html" },
  art153epc: { id: "art153epc", label: "Art. 153 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a153.html" },
};

export const eqePctVertieft: LearningExercise[] = [
  // 1 — Art. 31 PCT: Demand (single, level 1)
  makeSingle(sources, {
    id: "eqe-pctv-001",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 31 — Demand (Kap.-II-Antrag)",
    title: "Wer kann einen Demand stellen?",
    prompt:
      "Ein Anmelder moechte die internationale vorlaeufige Pruefung (Kapitel II) in Anspruch nehmen. Welche Voraussetzung muss er nach Art. 31 PCT zwingend erfuellen?",
    options: [
      "Er muss Staatsangehoeriger oder Gebietsansaessiger eines PCT-Vertragsstaats sein, der die Erklaerung nach Art. 31(4) PCT abgegeben hat.",
      "Er muss bereits einen positiven internationalen Recherchenbericht erhalten haben.",
      "Die internationale Anmeldung muss in englischer Sprache eingereicht worden sein.",
      "Der Demand kann nur zusammen mit der urspruenglichen PCT-Anmeldung eingereicht werden.",
    ],
    answer: 0,
    explanation:
      "Art. 31(2)(a) PCT verlangt, dass der Anmelder Staatsangehoeriger oder Gebietsansaessiger eines Vertragsstaats ist, der die entsprechende Erklaerung abgegeben hat. Die Ergebnisse des Recherchenberichts sind keine formale Voraussetzung fuer die Antragstellung; der Demand kann nach Erstattung des ISR bis spaetestens 22 Monate ab Prioritaet (bzw. 3 Monate nach Uebermittlung des ISR, je nachdem was spaeter eintritt) eingereicht werden.",
    sourceIds: ["pct31"],
    levelNumber: 1,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "pct", "kapitel-ii", "demand"],
  }),

  // 2 — Art. 31 PCT: Frist und IPEA (single, level 2)
  makeSingle(sources, {
    id: "eqe-pctv-002",
    pathId,
    // FIX (Orchestrator, R. 54bis.1(a) PCT): Option 0 auf den spaeteren Zeitpunkt korrigiert — 22 Monate ab Prioritaet (01.01.2025), da nach 3 Mon ab ISR (15.08.2024). Erklaerung war bereits korrekt; Distraktoren klar falsch.
    reviewStatus: "reviewed",
    topic: "PCT Art. 31 — Demand: Frist und IPEA",
    title: "Frist fuer den Demand und zustaendige IPEA",
    prompt:
      "Anmelderin A reicht am 1. März 2024 eine internationale PCT-Anmeldung ein (Prioritaet: 1. März 2023). Der ISR wird am 15. Mai 2024 uebermittelt. Bis wann kann A spaetestens den Demand einreichen, und welches Amt ist als IPEA zustaendig?",
    options: [
      "Spaetestens bis zum spaeteren der beiden Zeitpunkte — hier 22 Monate ab Prioritaet (1. Januar 2025), da dieser nach den 3 Monaten ab ISR-Uebermittlung liegt; IPEA ist die ISA oder ein anderes vereinbartes Amt nach Art. 31(2)(b) PCT.",
      "Spaetestens 30 Monate ab Prioritaet (1. September 2025); jedes nationale Amt ist als IPEA waehlbar.",
      "Spaetestens 18 Monate ab Prioritaet (1. September 2024); IPEA ist ausschliesslich das Anmeldeamt.",
      "Der Demand kann erst nach Erteilung des nationalen Patents eingereicht werden.",
    ],
    answer: 0,
    explanation:
      "Nach R. 54bis PCT muss der Demand spaetestens bis zum spaetereren der folgenden Zeitpunkte eingereicht werden: 3 Monate nach Uebermittlung des ISR oder 22 Monate ab Prioritaet. Im Sachverhalt ergibt 3 Monate nach ISR-Uebermittlung (15. Mai 2024) den 15. August 2024; 22 Monate ab Prioritaet ergeben den 1. Januar 2025 — massgeblich ist der spaetere Zeitpunkt, also 1. Januar 2025. Die zustaendige IPEA richtet sich nach Art. 31(2)(b) PCT und den Vereinbarungen zwischen Anmeldeamt und IPEA.",
    sourceIds: ["pct31"],
    levelNumber: 2,
    errorCategory: "Frist",
    skillTags: ["eqe", "pct", "kapitel-ii", "fristen", "ipea"],
  }),

  // 3 — Art. 34 PCT: Anspruchsaenderungen (single, level 2)
  makeSingle(sources, {
    id: "eqe-pctv-003",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 34 — Aenderungen im Kap.-II-Verfahren",
    title: "Aenderungen nach Art. 34 PCT: Umfang und Zeitpunkt",
    prompt:
      "Welche Aussage zu Aenderungen nach Art. 34 PCT trifft zu?",
    options: [
      "Der Anmelder kann waehrend der internationalen vorlaeufigen Pruefung Ansprueche, Beschreibung und Zeichnungen aendern, jedoch muss die IPEA dies bei der Erstellung des IPER beruecksichtigen.",
      "Aenderungen nach Art. 34 PCT sind auf die Ansprueche beschraenkt; Beschreibung und Zeichnungen duerfen nicht geaendert werden.",
      "Art. 34 PCT erlaubt Aenderungen nur nach Abschluss der internationalen vorlaeufigen Pruefung.",
      "Aenderungen nach Art. 34 PCT binden alle Bestimmungsaemter rechtlich und koennen nach Eintritt in die nationale Phase nicht mehr zurueckgenommen werden.",
    ],
    answer: 0,
    explanation:
      "Art. 34(2) PCT gibt dem Anmelder das Recht, waehrend der internationalen vorlaeufigen Pruefung Ansprueche, Beschreibung und Zeichnungen zu aendern. Die IPEA hat diese Aenderungen bei der Erstellung des internationalen vorlaeufigen Pruefungsberichts (IPER) zu beruecksichtigen. Der IPER selbst bindet die nationalen Aemter nicht (vgl. Art. 33(1) PCT: keine Bindungswirkung); die Aenderungen nach Art. 34 PCT sind damit von Art.-19-PCT-Aenderungen zu unterscheiden, die nur die Ansprueche betreffen.",
    sourceIds: ["pct34", "pct33"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "pct", "kapitel-ii", "aenderung", "art34"],
  }),

  // 4 — Art. 33 PCT: IPER-Kriterien (multi, level 2)
  makeMulti(sources, {
    id: "eqe-pctv-004",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 33 — IPER: Pruefungskriterien",
    title: "Welche Kriterien prueft die IPEA fuer den IPER?",
    prompt:
      "Welche der folgenden Kriterien sind nach Art. 33 PCT Gegenstand der internationalen vorlaeufigen Pruefung und damit des IPER? (Mehrere Antworten moeglich.)",
    options: [
      "Neuheit (Art. 33(2) PCT).",
      "Erfinderische Taetigkeit (Art. 33(3) PCT).",
      "Gewerbliche Anwendbarkeit (Art. 33(4) PCT).",
      "Formal korrekte Zeichengroesse nach den nationalen Designvorschriften.",
    ],
    answers: [0, 1, 2],
    explanation:
      "Art. 33(1) PCT nennt als Pruefungsgegenstand Neuheit, erfinderische Taetigkeit und gewerbliche Anwendbarkeit. Art. 33(2)-(4) PCT praezisieren diese drei Kriterien. Formale Zeichenvorschriften nationaler Aemter sind keine IPER-Kriterien. Entscheidend ist zudem Art. 33(1) Satz 2 PCT: Der IPER ist fuer die nationalen Aemter NICHT bindend — er ist eine unverbindliche Einschaetzung, die den Eintritt in die nationale Phase erleichtern soll.",
    sourceIds: ["pct33"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "pct", "iper", "kapitel-ii"],
  }),

  // 5 — Art. 33 PCT: Bindungswirkung (single, level 1, Fallkonstellation)
  makeSingle(sources, {
    id: "eqe-pctv-005",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 33 — IPER: Keine Bindungswirkung",
    title: "Fall: Positiver IPER — muss das EPA erteilen?",
    prompt:
      "Das EPA erhaelt als ausgewaehltes Amt eine Euro-PCT-Anmeldung. Der IPER der zustaendigen IPEA kommt zu dem Ergebnis, dass alle Ansprueche neu, erfinderisch und gewerblich anwendbar sind. Muss das EPA dieses Ergebnis akzeptieren und das Patent erteilen?",
    options: [
      "Nein — nach Art. 33(1) PCT und der staendigen EPA-Praxis ist der IPER fuer das EPA nicht bindend; das EPA fuehrt eine eigene materielle Pruefung durch.",
      "Ja — ein positiver IPER verpflichtet das ausgewaehlte Amt zur Erteilung ohne weitere Sachpruefung.",
      "Ja — aber nur, wenn der IPER durch eine EPA-akkreditierte IPEA erstellt wurde.",
      "Nein — positive IPER sind stets als negativ umzuwerten, weil das EPA eigene Masstaebe hat.",
    ],
    answer: 0,
    explanation:
      "Art. 33(1) Satz 2 PCT stellt ausdruecklich klar, dass der IPER die nationalen oder regionalen Aemter in keiner Weise bindet. Das EPA als Bestimmungsamt fuehrt nach Eintritt in die regionale Phase eine vollstaendige eigene Sachpruefung gemaess EPÜ durch. Der IPER kann in der Praxis die Pruefung beschleunigen, ist aber rechtlich unverbindlich.",
    sourceIds: ["pct33"],
    levelNumber: 1,
    errorCategory: "Irrtum",
    skillTags: ["eqe", "pct", "iper", "bindungswirkung"],
  }),

  // 6 — Art. 39 PCT: Nationale Phase Kap. II (cloze, level 1)
  makeCloze(sources, {
    id: "eqe-pctv-006",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 39 — Nationale Phase bei Kap.-II-Laendern",
    title: "Frist nationale Phase bei ausgewaehlten Aemtern",
    prompt:
      "Nach Art. 39(1)(a) PCT muessen Anmelder, die einen Demand gestellt haben, bei ausgewaehlten nationalen/regionalen Aemtern spaetestens ___ Monate ab dem Prioritaetsdatum in die nationale Phase eintreten.",
    options: ["18", "20", "30", "31"],
    answer: 2,
    explanation:
      "Art. 39(1)(a) PCT setzt fuer ausgewaehlte Aemter (Kapitel-II-Laender) eine Frist von 30 Monaten ab Prioritaetsdatum. Diese Frist entspricht der Grundfrist fuer Kapitel-I-Laender nach Art. 22 PCT. ACHTUNG: Das EPA als regionales Amt wendet gemaess R. 159.1 EPÜ eine eigene Frist von 31 Monaten an — diese geht als lex specialis vor und ist ein klassischer EQE-Stolperstein.",
    sourceIds: ["pct39", "r159epc"],
    levelNumber: 1,
    errorCategory: "Frist",
    skillTags: ["eqe", "pct", "nationale-phase", "kapitel-ii"],
  }),

  // 7 — Art. 19 PCT: Aenderungen nach ISR (single, level 2)
  makeSingle(sources, {
    id: "eqe-pctv-007",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 19 — Anspruchsaenderungen nach ISR",
    title: "Art.-19-Aenderung: Frist und Einreichungsstelle",
    prompt:
      "Ein Anmelder erhaelt am 10. Juni 2024 den internationalen Recherchenbericht (ISR); die Prioritaet datiert vom 1. Dezember 2022. Wo und bis wann kann er Anspruchsaenderungen nach Art. 19 PCT einreichen?",
    options: [
      "Beim Internationalen Buero (IB), spaetestens 2 Monate nach Uebermittlung des ISR (10. August 2024) oder 16 Monate ab Prioritaet (1. April 2024), je nachdem welcher Zeitpunkt spaeter liegt — hier also 10. August 2024.",
      "Bei der ISA, innerhalb von 30 Tagen nach Erstattung des ISR.",
      "Beim Anmeldeamt, spaetestens 18 Monate ab Prioritaet.",
      "Beim EPA als ausgewaehltem Amt, jederzeit vor Erteilung.",
    ],
    answer: 0,
    explanation:
      "Art. 19(1) PCT und R. 46.1 PCT: Aenderungen der Ansprueche sind beim Internationalen Buero einzureichen. Die Frist betraegt 2 Monate ab Uebermittlung des ISR oder 16 Monate ab Prioritaetsdatum, je nachdem welcher Zeitpunkt der spaetere ist. Im Sachverhalt: 16 Monate ab 1.12.2022 = 1.4.2024; 2 Monate ab 10.6.2024 = 10.8.2024. Massgeblich ist der spaetere Zeitpunkt: 10. August 2024. Art.-19-Aenderungen betreffen nur die Ansprueche, nicht Beschreibung oder Zeichnungen (anders als Art. 34 PCT).",
    sourceIds: ["pct19"],
    levelNumber: 2,
    errorCategory: "Frist",
    skillTags: ["eqe", "pct", "art19", "aenderung", "fristen"],
  }),

  // 8 — Art. 22 PCT: Nationale Phase Kap. I (single, level 1)
  makeSingle(sources, {
    id: "eqe-pctv-008",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 22 — Nationale Phase Kapitel I",
    title: "Grundfrist fuer den Eintritt in die nationale Phase (Kap. I)",
    prompt:
      "Welche Grundfrist setzt Art. 22(1) PCT fuer den Eintritt in die nationale Phase bei Bestimmungsaemtern, die kein Kapitel-II-Verfahren benoetigen?",
    options: [
      "30 Monate ab dem Prioritaetsdatum oder, falls keine Prioritaet beansprucht wird, ab dem internationalen Anmeldetag.",
      "12 Monate ab dem internationalen Anmeldetag.",
      "18 Monate ab dem Prioritaetsdatum.",
      "20 Monate ab dem internationalen Anmeldetag.",
    ],
    answer: 0,
    explanation:
      "Art. 22(1) PCT verankert die 30-Monats-Grundfrist. Sie laeuft ab dem Prioritaetsdatum oder, wenn keine Prioritaet in Anspruch genommen wurde, ab dem internationalen Anmeldetag. Viele Staaten haben die Frist auf 30 Monate verlaengert (frueher war die Grundfrist 20 Monate). Fuer das EPA als regionales Amt gilt R. 159.1 EPÜ mit 31 Monaten.",
    sourceIds: ["pct22"],
    levelNumber: 1,
    errorCategory: "Frist",
    skillTags: ["eqe", "pct", "nationale-phase", "kapitel-i"],
  }),

  // 9 — R. 159.1 EPÜ: EPA als Bestimmungsamt (multi, level 2, Fallkonstellation)
  makeMulti(sources, {
    id: "eqe-pctv-009",
    pathId,
    reviewStatus: "reviewed",
    topic: "R. 159 EPÜ — EPA als Bestimmungsamt (Euro-PCT)",
    title: "Fall: Welche Handlungen bei Eintritt in die regionale Phase beim EPA?",
    prompt:
      "Anmelderin B tritt nach einer PCT-Anmeldung (Prioritaet: 1. Januar 2023) in die regionale Phase beim EPA ein. Welche der folgenden Aussagen zu R. 159.1 EPÜ sind richtig? (Mehrere Antworten moeglich.)",
    options: [
      "Die Frist fuer den Eintritt in die regionale Phase betraegt 31 Monate ab Prioritaetsdatum, also spaetestens 1. August 2025.",
      "B muss innerhalb der Frist die Anmeldegebuehr, die Recherchengebuehr (soweit nicht schon erstattet) und ggf. Benenungsgebuehren entrichten.",
      "Wird keine Uebersetzung verlangt, weil die Anmeldung bereits in einer EPA-Amtssprache vorliegt, entfaellt diese Anforderung.",
      "Die Frist nach R. 159.1 EPÜ betraegt 30 Monate — identisch mit Art. 22 PCT.",
    ],
    answers: [0, 1, 2],
    explanation:
      "R. 159.1 EPÜ setzt ausdruecklich 31 Monate (nicht 30!) ab dem Prioritaetsdatum fuer den Eintritt in die regionale Phase beim EPA. Zu den vorzunehmenden Handlungen gehoeren nach R. 159.1 EPÜ insbesondere: Entrichtung der Anmeldegebuehr, ggf. Uebersetzung (wenn die Anmeldung nicht in einer EPA-Amtssprache vorliegt), und der Pruefungsantrag innerhalb der regulaeren Frist (Art. 94 EPÜ i.V.m. R. 70 EPÜ). Option D ist falsch: Die 31-Monate-Frist ist ein typischer EQE-Fallstrick gegenueber der PCT-Grundfrist von 30 Monaten.",
    sourceIds: ["r159epc", "pct22"],
    levelNumber: 2,
    errorCategory: "Frist",
    skillTags: ["eqe", "euro-pct", "r159", "regionale-phase"],
  }),

  // 10 — R. 90bis PCT: Ruecknahme (single, level 2)
  makeSingle(sources, {
    id: "eqe-pctv-010",
    pathId,
    reviewStatus: "reviewed",
    topic: "R. 90bis PCT — Ruecknahme",
    title: "Ruecknahme der internationalen Anmeldung nach R. 90bis PCT",
    prompt:
      "Anmelder C moechte seine internationale Anmeldung zuruecknehmen. Welche Aussage zu R. 90bis PCT ist richtig?",
    options: [
      "Die Ruecknahme der internationalen Anmeldung ist bis zum Ablauf von 30 Monaten ab dem Prioritaetsdatum moeglich und muss beim Anmeldeamt oder beim IB erklaert werden.",
      "Eine Ruecknahme ist nach PCT-Recht nie moeglich; der Anmelder muss jede Bestimmung einzeln fallen lassen.",
      "Die Ruecknahme ist nur nach Erstattung des IPER wirksam.",
      "Eine Ruecknahme nach R. 90bis PCT kann jederzeit und formlos per E-Mail erklaert werden.",
    ],
    answer: 0,
    explanation:
      "R. 90bis.1 PCT ermoeglicht die Ruecknahme der internationalen Anmeldung durch schriftliche Erklaerung gegenueber dem Anmeldeamt oder dem IB. Die Erklaerung muss vor Ablauf von 30 Monaten ab dem Prioritaetsdatum (bzw. dem internationalen Anmeldetag, falls keine Prioritaet) beim IB eingehen, um Wirkung vor allen Bestimmungsaemtern zu entfalten. R. 90bis erlaubt auch die Teilruecknahme (einzelne Bestimmungen, R. 90bis.2 PCT) oder die Ruecknahme des Demand (R. 90bis.4 PCT). Formfreiheit gilt nicht — es gelten die Formerfordernisse der R. 92 PCT.",
    sourceIds: ["r90bispct"],
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "pct", "ruecknahme", "r90bis"],
  }),

  // 11 — R. 90bis PCT: Ruecknahme Demand (single, level 2, Fallkonstellation)
  makeSingle(sources, {
    id: "eqe-pctv-011",
    pathId,
    reviewStatus: "reviewed",
    topic: "R. 90bis PCT — Ruecknahme des Demand",
    title: "Fall: Ruecknahme des Demand nach R. 90bis.4 PCT",
    prompt:
      "Anmelderin D hat einen Demand (Art. 31 PCT) gestellt, moechte das Kapitel-II-Verfahren aber wieder aufgeben. Nach R. 90bis.4 PCT: Bis wann muss die Ruecknahme des Demand beim IB eingegangen sein, um vor Weiterleitung des IPER an die Bestimmungsaemter zu wirken?",
    options: [
      "Die Ruecknahme muss vor dem Ablauf von 30 Monaten ab Prioritaet beim IB eingehen; nach Erstellung des IPER kann die Weiterleitung nicht mehr verhindert werden.",
      "Die Ruecknahme ist jederzeit vor der nationalen Erteilung moeglich.",
      "Der Demand kann nicht zurueckgenommen werden, sobald die IPEA die Pruefung begonnen hat.",
      "Die Ruecknahme des Demand ist nach PCT-Recht generell ausgeschlossen.",
    ],
    answer: 0,
    explanation:
      "R. 90bis.4(a) PCT: Die Ruecknahme des Demand ist moeglich, muss aber spaetestens vor dem Ablauf der 30-Monats-Frist (ab Prioritaet) beim IB eingegangen sein, damit sie Wirkung entfaltet. Ist der IPER bereits erstattet und an die ausgewaehlten Aemter weitergeleitet, kann die Weiterleitung nicht mehr rueckgaengig gemacht werden. Strategisch kann es sinnvoll sein, den Demand zurueckzunehmen, wenn der IPER voraussichtlich negativ wird und der Anmelder keine Werbewirkung eines negativen Berichts in den nationalen Phasen wuenscht.",
    sourceIds: ["r90bispct", "pct39"],
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "pct", "ruecknahme", "demand", "r90bis"],
  }),

  // 12 — Top-Up-Search: R. 164 EPÜ / Art. 153(7) EPÜ (single, level 2)
  makeSingle(sources, {
    id: "eqe-pctv-012",
    pathId,
    reviewStatus: "reviewed",
    topic: "R. 164 EPÜ / Art. 153(7) EPÜ — Ergaenzende Recherche (Top-Up-Search)",
    title: "Zweck und Grundlage der ergaenzenden Recherche",
    prompt:
      "In der regionalen Phase vor dem EPA wird eine Euro-PCT-Anmeldung geprueft. Das EPA stellt fest, dass die internationale Recherche nur einen Teil des beanspruchten Gegenstands abdeckt. Welche Norm ermaechtig das EPA zur ergaenzenden Recherche, und was ist deren Zweck?",
    options: [
      "Art. 153(7) EPÜ i.V.m. R. 164 EPÜ: Das EPA kann eine ergaenzende europaeische Recherche durchfuehren, um den Stand der Technik fuer nicht oder unvollstaendig recherchierte Teile der Anmeldung zu ermitteln.",
      "Art. 92 EPÜ allein: Das EPA wiederholt stets die gesamte internationale Recherche nach EPÜ-Standard.",
      "R. 159 EPÜ: Die ergaenzende Recherche ist Voraussetzung fuer den Eintritt in die regionale Phase.",
      "Art. 39 PCT: Das EPA darf als ausgewaehltes Amt selbst Recherchen vornehmen, wenn der IPER fehlt.",
    ],
    answer: 0,
    explanation:
      "Art. 153(7) EPÜ i.V.m. R. 164 EPÜ bilden die Rechtsgrundlage fuer die ergaenzende europaeische Recherche (Top-Up-Search) in der regionalen Phase. Wenn die internationale Recherche fuer das EPA nicht ausreicht (z.B. andere Datenbanken, andere Anspruchsinterpretation), erstellt das EPA einen ergaenzenden Recherchenbericht. Die Gebuehr hierfuer ist gesondert zu entrichten. Die ergaenzende Recherche ist konzeptionell von der Sachpruefung zu trennen.",
    sourceIds: ["r164epc", "art153epc"],
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "euro-pct", "top-up-search", "r164"],
  }),

  // 13 — PPH: kein PCT-Recht (single, level 2, Fallkonstellation)
  makeSingle(sources, {
    id: "eqe-pctv-013",
    pathId,
    // REVIEW eqe-pctv-013: Inhaltlich korrekt (PPH ist keine PCT-Norm, Aussage richtig). Anker-Hinweis (kein Fehler): einziger sourceId ist pct33 fuer eine PPH-Frage; Item behauptet aber NICHT, PPH sei pct33 (Distraktor B "geregelt in Art. 39 PCT" ist gerade falsch markiert). Anker tragbar, da auf den ISA-Written-Opinion/IPER-Kontext bezogen. Auf reviewed.
    reviewStatus: "reviewed",
    topic: "PPH — Patent Prosecution Highway",
    title: "Fall: Anwendbarkeit des PPH auf PCT-Anmeldungen",
    prompt:
      "Mandantin E hat eine PCT-Anmeldung eingereicht. Das EPA (als ISA) hat in seinem Written Opinion einen Anspruch als gewährbar bewertet. E moechte beim USPTO beschleunigt geprueft werden. Welche Aussage zum PPH ist richtig?",
    options: [
      "Der PPH ist kein PCT-Vertragsrecht, sondern ein bilaterales/multilaterales Verwaltungsabkommen; Voraussetzung ist, dass ein Amt mindestens einen Anspruch als gewaehrbar beurteilt hat, woraufhin das andere Amt die Pruefung beschleunigt.",
      "Der PPH ist in Art. 39 PCT geregelt und erfordert einen positiven IPER.",
      "Der PPH ersetzt das nationale Phasenverfahren vollstaendig.",
      "Das EPA erkennt den PPH als PCT-Norm an; eine positive Written Opinion der ISA genuegt fuer automatische Beschleunigung.",
    ],
    answer: 0,
    explanation:
      "Der Patent Prosecution Highway (PPH) ist kein Bestandteil des PCT-Vertrags, sondern beruht auf bilateralen oder multilateralen Verwaltungsvereinbarungen zwischen nationalen/regionalen Aemtern. Voraussetzung ist typischerweise, dass ein teilnehmendes Amt (hier das EPA als ISA oder als pruefendes Amt) mindestens einen Patentanspruch als gewaehrbar bewertet hat. Das andere Amt (hier das USPTO) kann auf Antrag des Anmelders die Pruefung beschleunigt durchfuehren. PPH ist damit ein prozeduales Instrument, das PCT-Ergebnisse nutzt, aber keine eigenstaendige PCT-Norm ist.",
    sourceIds: ["pct33"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "pct", "pph", "beschleunigung"],
  }),

  // 14 — Art. 19 vs. Art. 34: Abgrenzung (single, level 3, Fallkonstellation)
  makeSingle(sources, {
    id: "eqe-pctv-014",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Art. 19 vs. Art. 34 — Aenderungsfenster",
    title: "Fall: Welches Aenderungsregime gilt wann?",
    prompt:
      "Anmelder F erhaelt den ISR am 1. April 2024 und moechte (a) die Ansprueche noch in der internationalen Phase aendern, ohne einen Demand zu stellen, sowie (b) spaeter in einem Kapitel-II-Verfahren Beschreibung und Zeichnungen aendern. Welche Normen regeln jeweils Szenario (a) und (b)?",
    options: [
      "(a) Art. 19 PCT — Anspruchsaenderung beim IB nach ISR-Erstellung; (b) Art. 34 PCT — Aenderungen von Anspruechen, Beschreibung und Zeichnungen im Kapitel-II-Verfahren bei der IPEA.",
      "(a) Art. 34 PCT — fuer alle Aenderungen in der internationalen Phase; (b) Art. 19 PCT — nur fuer Beschreibungsaenderungen.",
      "(a) Art. 22 PCT — Anspruchsaenderungen nach ISR; (b) Art. 39 PCT — Beschreibungsaenderungen in der nationalen Phase.",
      "(a) Art. 19 PCT — aber nur in Verbindung mit einem Demand; (b) Art. 34 PCT — nur fuer Ansprueche.",
    ],
    answer: 0,
    explanation:
      "Art. 19 PCT ermoecklicht ausschliesslich Aenderungen der Ansprueche (nicht Beschreibung, nicht Zeichnungen) nach Erstellung des ISR, ohne dass ein Demand erforderlich ist. Das einreichende Buero ist das Internationale Buero. Art. 34 PCT hingegen gehoert zum Kapitel-II-Verfahren und erlaubt Aenderungen von Anspruechen, Beschreibung und Zeichnungen waehrend der internationalen vorlaeufigen Pruefung bei der IPEA. Beide Regime sind in der EQE gezielt als Abgrenzungsfrage relevant.",
    sourceIds: ["pct19", "pct34"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "pct", "art19", "art34", "aenderung"],
  }),

  // 15 — R. 159 EPÜ: 31-Monate-Falle (single, level 3, Fallkonstellation)
  makeSingle(sources, {
    id: "eqe-pctv-015",
    pathId,
    reviewStatus: "reviewed",
    topic: "R. 159 EPÜ — 31-Monate-Frist: Klassischer Fallstrick",
    title: "Fall: Fristversaeumnis 30 vs. 31 Monate beim EPA",
    prompt:
      "Anwalt G erinnert sich, dass die nationale Phase-Frist 30 Monate ab Prioritaet betraegt (Art. 22 PCT) und leitet den Eintritt beim EPA exakt am letzten Tag des 30. Monats ein. Wie bewertet das EPA diesen Schritt?",
    options: [
      "Das EPA behandelt die Einleitung als zu frueh — nicht als Fehler, aber die massgebliche Frist nach R. 159.1 EPÜ betraegt 31 Monate. G hat keine Frist versaeumt; er haette noch einen Monat Zeit gehabt.",
      "Das EPA weist die Anmeldung ab, weil der Eintritt einen Tag zu spaet war.",
      "Der Eintritt am 30. Monat ist korrekt, weil R. 159 EPÜ auf Art. 22 PCT verweist.",
      "Das EPA akzeptiert den Eintritt, erhebt aber eine Verspaetungsgebuehr fuer das verfrueht eingereichte Dokument.",
    ],
    answer: 0,
    explanation:
      "R. 159.1 EPÜ setzt fuer den Eintritt in die regionale Phase beim EPA eine Frist von 31 Monaten, nicht 30 Monate (wie Art. 22 PCT fuer andere Bestimmungsaemter). G hat damit die Anmeldung einen Monat frueh eingereicht — kein Problem, aber der Irrtum haette im umgekehrten Fall (Eintritt am 31. Monat in einem 30-Monat-Staat) fatal sein koennen. Diese 1-Monats-Differenz zwischen PCT-Grundfrist und EPA-Frist ist ein klassischer EQE-M4-Fallstrick.",
    sourceIds: ["r159epc", "pct22"],
    levelNumber: 3,
    errorCategory: "Frist",
    skillTags: ["eqe", "euro-pct", "r159", "31-monate", "fallstrick"],
  }),

  // 16 — Fallkonstellation: Kombination Art. 31/34/33/39 (single, level 3)
  makeSingle(sources, {
    id: "eqe-pctv-016",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Kap. II — Vollstaendige Fallkonstellation",
    title: "Fall: Strategie Kap.-II-Verfahren mit negativem Written Opinion",
    prompt:
      "Anmelder H erhaelt einen negativen Written Opinion des EPA (als ISA). Er stellt daraufhin einen Demand (Art. 31 PCT) und reicht Aenderungen bei der IPEA ein (Art. 34 PCT). Der IPER faellt positiv aus. H tritt dann in die regionale Phase beim EPA ein. Welche Aussage trifft zu?",
    options: [
      "Das EPA ist durch den positiven IPER nicht gebunden und kann die Neuheit oder erfinderische Taetigkeit eigenstaendig anders bewerten; der IPER kann aber faktisch als Argumentation genuetzt werden.",
      "Das EPA ist durch den positiven IPER gebunden und muss erteilen, wenn H alle formellen Anforderungen der R. 159 EPÜ erfuellt.",
      "H kann die Art.-34-Aenderungen nicht in die regionale Phase uebernehmen; es gilt die urspruengliche Fassung.",
      "Ein positiver IPER hat keine verfahrensrechtliche Wirkung und kann nicht eingereicht werden.",
    ],
    answer: 0,
    explanation:
      "Art. 33(1) Satz 2 PCT: Der IPER bindet die nationalen/regionalen Aemter nicht. Das EPA fuehrt eine eigenstaendige Sachpruefung gemaess EPÜ durch. In der Praxis kann ein positiver IPER jedoch als Argument in der Pruefungsphase eingebracht werden und zur Beschleunigung beitragen. Die Art.-34-Aenderungen wirken in der internationalen Phase; ob sie in die regionale Phase uebernommen werden (und ob R. 159 EPÜ zusaetzliche Anpassungen erfordert), haengt vom jeweiligen regionalen Recht ab.",
    sourceIds: ["pct33", "pct34", "pct31", "r159epc"],
    levelNumber: 3,
    errorCategory: "Irrtum",
    skillTags: ["eqe", "pct", "kapitel-ii", "strategie", "bindungswirkung"],
  }),

  // 17 — Ordering: Zeitlicher Ablauf Kap.-II-Verfahren
  makeOrdering(sources, {
    id: "eqe-pctv-017",
    pathId,
    reviewStatus: "reviewed",
    topic: "PCT Kap. II — Zeitlicher Verfahrensablauf",
    title: "Ordering: Ablauf des Kapitel-II-Verfahrens",
    prompt:
      "Bringen Sie die folgenden Schritte des PCT-Kapitel-II-Verfahrens in die richtige zeitliche Reihenfolge.",
    orderItems: [
      "Internationale Anmeldung und Zuerkennung des internationalen Anmeldetags (Art. 11 PCT).",
      "Erstattung des internationalen Recherchenberichts (ISR) durch die ISA; ggf. Anspruchsaenderungen nach Art. 19 PCT beim IB.",
      "Einreichung des Demand bei der IPEA nach Art. 31 PCT (spaetestens 22 Monate ab Prioritaet oder 3 Monate nach ISR-Uebermittlung, je nachdem was spaeter eintritt).",
      "Ggf. Einreichung von Aenderungen nach Art. 34 PCT bei der IPEA waehrend der internationalen vorlaeufigen Pruefung.",
      "Erstattung des internationalen vorlaeufigen Pruefungsberichts (IPER) nach Art. 33 PCT durch die IPEA.",
      "Eintritt in die nationale/regionale Phase bei ausgewaehlten Aemtern gemaess Art. 39 PCT (Frist: 30 Monate ab Prioritaet; EPA: 31 Monate nach R. 159.1 EPÜ).",
    ],
    explanation:
      "Der zeitliche Ablauf folgt Art. 11 → Art. 19 (ggf.) → Art. 31 → Art. 34 (ggf.) → Art. 33 (IPER) → Art. 39 (nationale Phase). Entscheidend fuer die EQE ist das Zusammenspiel der Fristen: ISR-Uebermittlung triggert Art.-19-Fenster; Demand setzt Kapitel-II-Verfahren in Gang; Art.-34-Aenderungen sind vor IPER-Erstattung einzureichen; IPER ist nicht bindend; EPA-Eintritt nach R. 159.1 EPÜ 31 Monate (nicht 30!).",
    sourceIds: ["pct31", "pct19", "pct34", "pct33", "pct39", "r159epc"],
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["eqe", "pct", "kapitel-ii", "ordering", "verfahrensablauf"],
  }),

  // 18 — PPH vs. PCT: Abgrenzungsfrage (multi, level 2)
  makeMulti(sources, {
    id: "eqe-pctv-018",
    pathId,
    reviewStatus: "reviewed",
    topic: "PPH — Abgrenzung zu PCT-Kapitel II",
    title: "PPH und PCT: Gemeinsamkeiten und Unterschiede",
    prompt:
      "Welche der folgenden Aussagen zum Verhaeltnis zwischen PPH und PCT-Kapitel-II-Verfahren sind richtig? (Mehrere Antworten moeglich.)",
    options: [
      "Der PPH beruht nicht auf dem PCT-Vertrag, sondern auf bilateral/multilateral vereinbarten Verwaltungsabkommen.",
      "Sowohl PPH als auch Art. 33 PCT zielen auf eine fruehzeitige Einschaetzung der Patentierbarkeit, haben aber unterschiedliche Rechtsgrundlagen.",
      "PPH und PCT Kapitel II sind funktional identisch und koennen nicht gleichzeitig genutzt werden.",
      "Ein positiver IPER kann die Voraussetzungen fuer einen PPH-Antrag bei einem teilnehmenden Amt erleichtern.",
    ],
    answers: [0, 1],
    explanation:
      "Der PPH ist kein PCT-Norminstrument, sondern ein verwaltungsrechtliches Kooperationsabkommen. Er und Kapitel II teilen das Ziel, Redundanz bei der Patentpruefung zu reduzieren und Anmeldungen zu beschleunigen — aber auf vollig unterschiedlichen Rechtsgrundlagen. Ein positiver IPER (Art. 33 PCT) kann faktisch als Ausgangsbasis fuer einen PPH-Antrag dienen, ist aber keine gesetzliche Voraussetzung hierfuer. Option C ist falsch: beide Instrumente koennen konzeptionell kombiniert werden, auch wenn die Praxis variiert.",
    sourceIds: ["pct33", "r159epc"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "pct", "pph", "kapitel-ii", "abgrenzung"],
  }),
];
